import { Visibility, VisibilityOff, LocationOn } from "@mui/icons-material";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import ModalComp from "../ModalComp";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Icon } from "leaflet";
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMapEvents,
} from "react-leaflet";
import { enqueueSnackbar } from "notistack";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const CustomTextField = styled(TextField)(
  ({ theme, paddingTop, inputType }) => ({
    "& .MuiOutlinedInput-root": {
      minHeight: "56px",
      paddingTop: paddingTop,
      "& fieldset": {
        borderRadius: "8px",
        borderColor: "gray", // Default border color
      },
      "&:hover fieldset": {
        borderColor: "gray", // Border color when hovering
      },
      "&.Mui-focused fieldset": {
        borderColor: "gray", // Border color when focused
      },
      "&.Mui-error fieldset": {
        borderColor: "#d32f2f !important",
      },
      "&.Mui-error:hover fieldset": {
        borderColor: "#d32f2f !important",
      },
      ...(inputType === "date" && {
        paddingRight: "10px",
      }),
    },
    "& .MuiInputLabel-root": {
      color: "gray", // Default label color
      "&.Mui-error": {
        color: "#d32f2f !important",
      },
      whiteSpace: "break-spaces",
      position: "absolute", // Position the label absolutely to manage alignment
      left: "16px", // Align label with the text input padding
      top: "50%", // Vertically center label
      transform: "translateY(-50%) scale(1)", // Adjust to align with input
      transformOrigin: "left", // Label scales from the left
      transition: "all 0.2s ease-in-out", // Smooth transition for focus effects
      lineHeight: "1.5", // Set label line height
      pointerEvents: "none", // Prevent label from being clickable
      width: "max-content",
      ...(inputType === "date" && {
        color: "gray", // Label color when focused
        top: "-10px", // Move label above input when focused
        transform: "translateY(0) scale(0.75)", // Shrink and position label above input
        backgroundColor: "white", // Set background color for date input
        paddingLeft: "10px",
        paddingRight: "10px",
      }),
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "gray", // Label color when focused
      top: "-10px", // Move label above input when focused
      transform: "translateY(0) scale(0.75)", // Shrink and position label above input
    },
    "& .MuiInputLabel-shrink": {
      top: "-10px", // Move label above input when input has value
      transform: "translateY(0) scale(0.75)", // Shrink the label and move it above the input
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 16px", // Adjust input padding for better alignment
      lineHeight: "1.5", // Ensuring proper vertical alignment
      paddingRight: "0px",
    },
    "& .MuiFormHelperText-root": {
      marginTop: "4px", // Adjust margin for helper text below input
    },
  })
);

const CustomInput = ({
  value,
  onChange,
  label,
  type,
  name,
  required,
  error,
  helperText,
  disabled,
  endLabel,
  className,
  maxlength,
  min,
  max,
  inputMode,
  allowFutureDates,
  allowOnlyFutureDates = false,
  copy = false,
}) => {
  const [showPassword, setShowPassword] = React.useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(value || "");
  const [map, setMap] = useState(null);
  const mapRef = useRef(null);
  const [markerPosition, setMarkerPosition] = useState({
    lat: 12.983222,
    lng: 77.546188,
  });
  const [center, setCenter] = useState({
    lat: 12.983222,
    lng: 77.546188,
  });

  // Effect to focus map on marker position change
  useEffect(() => {
    if (mapRef.current && markerPosition) {
      try {
        mapRef.current.flyTo(
          [markerPosition.lat, markerPosition.lng],
          mapRef.current.getZoom(),
          {
            duration: 1,
          }
        );
      } catch (error) {
        console.error("Error flying to position:", error);
      }
    }
  }, [markerPosition]);

  // Initialize with value if provided
  useEffect(() => {
    if (value) {
      try {
        const [lat, lng] = value.split(",").map(Number);
        if (!isNaN(lat) && !isNaN(lng)) {
          setMarkerPosition({ lat, lng });
          setCenter({ lat, lng });
        }
      } catch (e) {
        console.error("Error parsing location value:", e);
      }
    }
  }, [value]);

  const MapClickHandler = ({ setMarkerPosition, setSelectedLocation }) => {
    const map = useMapEvents({
      click: (e) => {
        const newPos = { lat: e.latlng.lat, lng: e.latlng.lng };
        setMarkerPosition(newPos);
        setSelectedLocation(`${newPos.lat}, ${newPos.lng}`);
      },
    });


    // Store map reference when created
    useEffect(() => {
      mapRef.current = map;
    }, [map]);


    return null;
  };

  const handleOpenModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const handleConfirmLocation = () => {
    onChange({ target: { name, value: selectedLocation } });
    closeModal();
  };

  const handleSearch = () => {
    const searchInput = document.getElementById("location-search").value;
    if (searchInput) {
      fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          searchInput
        )}`
      )
        .then((response) => response.json())
        .then((data) => {
          if (data.length > 0) {
            const firstResult = data[0];
            const newPos = {
              lat: parseFloat(firstResult.lat),
              lng: parseFloat(firstResult.lon),
            };
            setCenter(newPos);
            setMarkerPosition(newPos);
            setSelectedLocation(`${newPos.lat}, ${newPos.lng}`);
            if (map) map.flyTo([newPos.lat, newPos.lng], 15);
          }
        })
        .catch((error) => {
          console.error("Search error:", error);
          enqueueSnackbar("Error searching for location", { variant: "error" });
        });
    }
  };

  const handleLocateMe = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newPos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setCenter(newPos);
          setMarkerPosition(newPos);
          setSelectedLocation(`${newPos.lat}, ${newPos.lng}`);
          if (map) map.flyTo([newPos.lat, newPos.lng], 15);
        },
        (error) => {
          console.error("Error getting location:", error);
          enqueueSnackbar("Could not get your current location", {
            variant: "error",
          });
        }
      );
    } else {
      enqueueSnackbar("Geolocation is not supported by your browser", {
        variant: "error",
      });
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const today = new Date().toISOString().split("T")[0];

  const calculatePaddingTop = (label) => {
    const lineCount = label.split("\n").length;
    let basePadding;

    // Adjust base padding based on label length in smaller increments
    if (label.length >= 500) {
      basePadding = 70;
    } else if (label.length >= 350) {
      basePadding = 50;
    } else if (label.length >= 200) {
      basePadding = 30;
    } else if (label.length >= 100) {
      basePadding = 20;
    } else {
      basePadding = 10;
    }

    // Additional padding for each line break
    const extraPadding = (lineCount - 1) * 15; // Increase line break padding if needed

    return `${basePadding + extraPadding}px`;
  };

  const paddingTop = calculatePaddingTop(label); // Get dynamic paddingTop value

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <CustomTextField
        className={`w-[100%] ${className}`}
        onCopy={(e) => {
          if (copy) {
            e.preventDefault();
            enqueueSnackbar("Copying Text is disabled");
          }
        }}
        onClick={(e) => {
          if (type === "location on map") {
            handleOpenModal();
            return;
          }
          if (type === "date" && typeof e.target.showPicker === "function") {
            try {
              e.target.showPicker();
            } catch {
              // showPicker requires a direct user gesture
            }
          }
        }}
        required={required}
        disabled={disabled}
        error={error}              
        helperText={helperText}
        InputLabelProps={{
          style: {
            color: "gray",
          },
        }}
        inputProps={{
          max: type === "date" && !allowFutureDates ? today : undefined, // Disable future dates for non-allowed fields
          min: type === "date" && allowOnlyFutureDates ? today : min,
          maxLength: maxlength ? maxlength : undefined,
          inputMode: inputMode ? inputMode : undefined,
        }}
        id="outlined-basic"
        value={value !== undefined ? value : type === "date" ? "" : ""}
        onChange={onChange}
        onWheel={(e) => {
          if (type === "number") {
            e.target.blur();
          }
        }}
        type={type === "password" ? (showPassword ? "text" : "password") : type}
        label={label}
        variant="outlined"
        onKeyDown={(e) => {
          if (type === 'number' && ['e', 'E', '+', '-'].includes(e.key)) {
            e.preventDefault();
          }
        }
        }
        name={name}
        paddingTop={paddingTop}
        inputType={type} // Pass the type as inputType for conditional styling
        readOnly={type === "location on map"}
      />
      <div
        style={{
          position: "absolute",
          top: "50%",
          right: "16px", // Adjust spacing from the right edge of the field
          transform: "translateY(-50%)", // Vertically center the button
        }}
      >
        {endLabel}
        {type === "password" && (
          <IconButton
            aria-label="toggle password visibility"
            onClick={handleClickShowPassword}
            onMouseDown={handleMouseDownPassword}
            edge="end"
          >
            {showPassword ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        )}
        {type === "location on map" && (
          <IconButton onClick={handleOpenModal} edge="end">
            <MyLocationIcon />
          </IconButton>
        )}
      </div>

      <ModalComp
        closeModal={closeModal}
        padding="10px"
        modalIsOpen={isOpen}
        width="90%"
        maxHeight="600px"
      >
        <Box sx={{ padding: "20px" }}>
          <Typography variant="h6" align="center" sx={{ marginBottom: "20px" }}>
            Select Your Location
          </Typography>

          <Box sx={{ marginBottom: "20px", display: "flex", gap: "10px" }}>
            <input
              type="text"
              placeholder="Search for a location..."
              style={{
                flex: 1,
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #ccc",
              }}
              id="location-search"
            />
            <button
              onClick={handleSearch}
              className="bg-blue-500 text-white py-2 px-4 rounded"
            >
              Search
            </button>
          </Box>

          <MapContainer
            center={[center.lat, center.lng]}
            zoom={13}
            style={{ height: "400px", width: "100%", borderRadius: "5px" }}
            // whenCreated={setMap}
            whenCreated={(map) => (mapRef.current = map)}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; <a href=''>GuideStar India</a> "
            />
            <MapClickHandler
              setMarkerPosition={setMarkerPosition}
              setSelectedLocation={setSelectedLocation}
            />
            <Marker position={[markerPosition.lat, markerPosition.lng]}>
              <Popup>
                {`Latitude: ${markerPosition.lat}, Longitude: ${markerPosition.lng}`}
              </Popup>
            </Marker>
          </MapContainer>

          <Box sx={{ textAlign: "center", paddingTop: "15px" }}>
            <button
              onClick={handleLocateMe}
              className="bg-green-500 text-white py-2 px-4 rounded mr-2"
            >
              My Current Location
            </button>


            <button
              onClick={handleConfirmLocation}
              className="bg-blue-500 text-white py-2 px-4 rounded"
            >
              Confirm Location
            </button>
          </Box>
        </Box>
      </ModalComp>
    </div>
  );
};

export default CustomInput;
